import { cloneElement, isValidElement, useEffect, useRef, type CSSProperties, type ReactElement, type ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { color, font, stageTheme, formTheme } from '../styles/theme';
import { IconLock, IconSpinner, IconClose } from './icons';

/** The design's white rounded panel. */
export function Card({
  children,
  style,
  pad = 20,
}: {
  children: ReactNode;
  style?: CSSProperties;
  pad?: number | string;
}) {
  return (
    <div
      style={{
        background: color.white,
        border: `1px solid ${color.line}`,
        borderRadius: 20,
        padding: pad,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Page heading — 27px Poppins with an optional lead line, as in the design. */
export function PageTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div>
      <h1
        style={{
          fontFamily: font.display,
          fontWeight: 600,
          fontSize: 27,
          color: color.navy,
          margin: 0,
        }}
      >
        {children}
      </h1>
      {sub && <p style={{ fontSize: 13.5, color: color.muted, margin: '5px 0 0' }}>{sub}</p>}
    </div>
  );
}

/** Panel heading — 17px Poppins, used inside cards. */
export function CardTitle({
  children,
  size = 17,
  style,
}: {
  children: ReactNode;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <h2
      style={{
        fontFamily: font.display,
        fontWeight: 600,
        fontSize: size,
        color: color.navy,
        margin: 0,
        ...style,
      }}
    >
      {children}
    </h2>
  );
}

/** The rounded tinted square that fronts every card header in the design. */
export function CardIcon({ children, size = 36 }: { children: ReactNode; size?: number }) {
  return (
    <span
      style={{
        display: 'flex',
        width: size,
        height: size,
        borderRadius: size > 32 ? 11 : 10,
        background: color.tint,
        alignItems: 'center',
        justifyContent: 'center',
        color: color.blue,
        flex: 'none',
      }}
    >
      {children}
    </span>
  );
}

/** Column label above a table, uppercase and tracked out. */
export function ColHead({ children, align }: { children?: ReactNode; align?: 'right' }) {
  return (
    <span
      style={{
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: color.muted,
        textAlign: align,
      }}
    >
      {children}
    </span>
  );
}

export function StageBadge({ stage, label }: { stage: keyof typeof stageTheme; label: string }) {
  const t = stageTheme[stage];
  return (
    <span
      style={{
        background: t.bg,
        color: t.fg,
        fontSize: 11.5,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {label}
    </span>
  );
}

export function FormPill({ form, label }: { form: keyof typeof formTheme; label: string }) {
  const t = formTheme[form];
  return (
    <span
      style={{
        background: t.bg,
        color: t.fg,
        fontSize: 11.5,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {label}
    </span>
  );
}

/** The design's pill filter — navy when active, outlined when not. */
export function Chip({
  label,
  active,
  onClick,
  title,
}: {
  label: ReactNode;
  active: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={active ? undefined : 'hov-line'}
      style={{
        border: `1px solid ${active ? color.navy : color.line}`,
        background: active ? color.navy : color.white,
        color: active ? color.white : color.ink,
        fontSize: 12.5,
        fontWeight: 700,
        padding: '6px 14px',
        borderRadius: 999,
        cursor: 'pointer',
        maxWidth: 260,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Marks a row whose patient-identifying fields the API withheld.
 *
 * Shown rather than silently rendering dashes: an operator who cannot tell
 * "hidden from me" from "not provided" will chase records that are fine.
 */
export function RedactedBadge() {
  return (
    <span
      title="Patient-identifying fields are hidden for your role"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: '#eef1f5',
        color: color.muted,
        fontSize: 10.5,
        fontWeight: 800,
        padding: '3px 8px',
        borderRadius: 999,
      }}
    >
      <IconLock size={11} /> Hidden
    </span>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  type = 'button',
  style,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'navy' | 'ghost' | 'soft' | 'danger';
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  title?: string;
}) {
  const variants = {
    primary: { background: color.blue, color: color.white, border: 'none' },
    navy: { background: color.navy, color: color.white, border: 'none' },
    ghost: { background: 'transparent', color: color.ink, border: `1px solid ${color.line}` },
    soft: { background: color.tint, color: color.blueDeep, border: 'none' },
    danger: { background: 'transparent', color: color.red, border: `1.5px solid ${color.red}` },
  } as const;

  const hover = {
    primary: 'hov-blue',
    navy: 'hov-navy',
    ghost: 'hov-line',
    soft: 'hov-tint2',
    danger: 'hov-danger-bg',
  } as const;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={disabled ? undefined : hover[variant]}
      style={{
        ...variants[variant],
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 20px',
        borderRadius: 999,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Small circular icon button — the ‹ › pagers and the ± steppers. */
export function RoundButton({
  children,
  onClick,
  title,
  disabled,
  size = 28,
  tone = 'muted',
}: {
  children: ReactNode;
  onClick?: () => void;
  title?: string;
  disabled?: boolean;
  size?: number;
  tone?: 'muted' | 'ink';
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={disabled ? undefined : 'hov-line-blue'}
      style={{
        display: 'flex',
        width: size,
        height: size,
        borderRadius: '50%',
        border: `1px solid ${color.line}`,
        background: color.white,
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color: tone === 'ink' ? color.ink : color.muted,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1,
        flex: 'none',
      }}
    >
      {children}
    </button>
  );
}

export const inputStyle: CSSProperties = {
  border: `1px solid ${color.line}`,
  borderRadius: 12,
  padding: '10px 13px',
  fontSize: 14,
  color: color.ink,
  background: color.white,
  outline: 'none',
  width: '100%',
};

export const selectStyle: CSSProperties = {
  ...inputStyle,
  fontWeight: 700,
  fontSize: 13.5,
  cursor: 'pointer',
  appearance: 'none',
  paddingRight: 32,
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6'><path d='M1 1l4 4 4-4' stroke='%235b6b7c' fill='none' stroke-width='1.5'/></svg>\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};

/**
 * A select worn as a pill, for a choice that sits on a row or a card beside
 * the outlined action buttons rather than in a form.
 */
export const pillSelectStyle: CSSProperties = {
  ...selectStyle,
  width: 'auto',
  maxWidth: '100%',
  borderRadius: 999,
  padding: '5px 26px 5px 12px',
  fontSize: 11.5,
};

/**
 * Labelled form field — the design wraps every input in one of these.
 *
 * `error` is what the server said about this field, in text, under its own
 * label (WCAG 3.3.1: the item in error is identified and described in text,
 * never by colour alone). The message sits inside the `<label>`, so a screen
 * reader reads it with the field rather than as a loose sentence, and the
 * control itself is marked `aria-invalid`.
 */
export function Field({
  label,
  hint,
  error,
  children,
  style,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const control =
    error && isValidElement(children)
      ? cloneElement(children as ReactElement<{ 'aria-invalid'?: boolean }>, { 'aria-invalid': true })
      : children;

  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        fontSize: 12.5,
        fontWeight: 700,
        color: color.muted,
        minWidth: 0,
        ...style,
      }}
    >
      <span>
        {label}
        {hint && <span style={{ fontWeight: 600, color: color.muted }}> {hint}</span>}
      </span>
      {control}
      {error && (
        <span role="alert" style={{ fontSize: 12, fontWeight: 700, color: color.red }}>
          {error}
        </span>
      )}
    </label>
  );
}

/** Right-hand slide-over used by the inquiry and participant details. */
export function Modal({
  header,
  children,
  footer,
  onClose,
  width = 560,
  label,
}: {
  header: ReactNode;
  children: ReactNode;
  /** Pinned below the scroll area — for actions that must stay reachable. */
  footer?: ReactNode;
  onClose: () => void;
  width?: number;
  label: string;
}) {
  const panel = useRef<HTMLDivElement>(null);

  /**
   * Hold the page still while the drawer is open.
   *
   * Without this the page keeps scrolling behind the overlay: the sticky header
   * slides away under the backdrop, and a scroll gesture aimed at the drawer's
   * own content carries on scrolling the list underneath once the drawer hits
   * its end. Locking `body` is the fix, and the padding compensates for the
   * scrollbar the lock removes — otherwise the whole layout jumps a few pixels
   * left the moment a drawer opens.
   */
  useEffect(() => {
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const gutter = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, []);

  /**
   * Escape closes, and focus moves into the panel so that it does.
   *
   * A dialog that can only be dismissed by hitting a small × is a dialog people
   * get stuck in. Focus is restored to whatever opened the drawer, so keyboard
   * users land back on the row they clicked rather than at the top of the page.
   */
  /*
    `onClose` is read through a ref so the effect runs once. Every caller
    passes an inline arrow, so depending on it re-ran the effect on each parent
    re-render — a list refetching behind the dialog was enough — which handed
    focus back to the opener and then to the panel. Tolerable while the panel
    was read-only; with a reply being typed inside it, it pulled the caret out
    of the textarea mid-sentence.
  */
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);

  /**
   * Rendered into `document.body`, not in place.
   *
   * Sections wrap their content in `.fade-in`, whose keyframes animate a
   * transform with `fill-mode: both` — so an identity transform stays live on
   * the element forever. A transformed ancestor becomes the containing block
   * for `position: fixed`, which trapped this overlay inside the content area:
   * the backdrop stopped at the sidebar instead of covering the viewport, the
   * dimming looked uneven, and the sidebar stayed clickable behind a supposedly
   * modal dialog.
   *
   * Escaping to the body fixes all three at once, and keeps working whatever a
   * section decides to animate later.
   */
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Breathing room so the panel never touches the edge on a short window.
        padding: 'clamp(16px, 4vh, 40px) 16px',
      }}
    >
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(18,38,63,0.35)' }}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="fade-in"
        style={{
          outline: 'none',
          // Above the backdrop, which is absolutely positioned behind it.
          position: 'relative',
          width: `min(${width}px, 100%)`,
          // Cap the height rather than the content: a long record scrolls
          // inside the panel instead of pushing the header and the actions
          // off-screen.
          maxHeight: '100%',
          background: color.white,
          borderRadius: 20,
          boxShadow: '0 24px 60px rgba(18,38,63,0.24)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '20px 24px',
            borderBottom: `1px solid ${color.line}`,
            flex: 'none',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>{header}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="hov-tint"
            style={{
              background: 'transparent',
              border: 'none',
              color: color.muted,
              cursor: 'pointer',
              lineHeight: 1,
              padding: 6,
              borderRadius: 8,
              display: 'flex',
              flex: 'none',
            }}
          >
            <IconClose />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 24 }}>{children}</div>
        {footer && (
          <div
            style={{
              flex: 'none',
              borderTop: `1px solid ${color.line}`,
              background: color.bg,
              padding: '14px 24px',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}


/** The tinted key/value tile used inside the modal. */
export function Tile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ background: color.tint, borderRadius: 14, padding: '12px 14px', minWidth: 0 }}>
      <p
        style={{
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: color.muted,
          margin: '0 0 4px',
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: 14, fontWeight: 700, color: color.ink, margin: 0 }}>{value}</p>
    </div>
  );
}

/** Section eyebrow inside a drawer. */
export function Eyebrow({ children, tone }: { children: ReactNode; tone?: 'blue' }) {
  return (
    <p
      style={{
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: tone === 'blue' ? color.blueDeep : color.muted,
        margin: '0 0 6px',
      }}
    >
      {children}
    </p>
  );
}

/**
 * A confirmation bubble anchored to the control that opened it.
 *
 * Moving someone along the pipeline is not a private bookkeeping edit: the
 * status decides the line that person is shown about their own application, so
 * a mis-click on a dense board tells a real applicant something untrue. This
 * keeps the decision beside the button — and shows the sentence they will be
 * given — rather than throwing a page-level dialog over the work.
 *
 * `open` is the caller's, because the board opens one of these per card and
 * only ever wants one of them up at a time.
 */
export function Confirm({
  open,
  title,
  detail,
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel,
  align = 'left',
  children,
}: {
  open: boolean;
  title: string;
  detail?: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  const wrap = useRef<HTMLSpanElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const yes = useRef<HTMLButtonElement>(null);
  /**
   * Where the bubble sits, in window terms.
   *
   * It is drawn through a portal rather than inside the control, because the
   * control usually sits in something that clips — a table card, a scrolling
   * board — and a bubble drawn inside it lost its first line, the question
   * itself, on the top rows. Above the control by default, so it never covers
   * what the person is about to act on; a control near the top of the window
   * (a page header's Start meeting) would put it off screen, so there it
   * opens below. Re-measured on scroll and resize so it stays on its control.
   */
  const [pos, setPos] = useState<{
    left: number;
    width: number;
    below: boolean;
    top?: number;
    bottom?: number;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    const place = (): void => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(262, Math.round(window.innerWidth * 0.76));
      const below = r.top < 220;
      const wanted = align === 'right' ? r.right - width : r.left;
      const left = Math.max(8, Math.min(wanted, window.innerWidth - width - 8));
      setPos(
        below
          ? { left, width, below, top: r.bottom + 10 }
          : { left, width, below, bottom: window.innerHeight - r.top + 10 },
      );
    };
    place();

    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (!wrap.current?.contains(t) && !box.current?.contains(t)) onCancel();
    };
    /*
      Captured, not bubbled: inside the participant modal the same key would
      otherwise reach the dialog's own handler and close the whole panel, so
      one Escape would dismiss both the question and the record behind it.
    */
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, align, onCancel]);

  // The bubble exists only once it has been placed, so focus follows that.
  const placed = open && pos !== null;
  useEffect(() => {
    if (placed) yes.current?.focus();
  }, [placed]);

  const below = pos?.below ?? false;

  return (
    <span ref={wrap} style={{ position: 'relative', display: 'inline-flex', maxWidth: '100%' }}>
      {children}
      {placed &&
        createPortal(
        <div
          ref={box}
          role="dialog"
          aria-label={title}
          // A React event from a portal still climbs the component tree, and
          // the board card underneath opens the record on click.
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: pos.left,
            ...(pos.below ? { top: pos.top } : { bottom: pos.bottom }),
            zIndex: 200,
            width: pos.width,
            background: color.white,
            border: `1px solid ${color.line}`,
            borderRadius: 14,
            boxShadow: '0 14px 34px rgba(18,38,63,0.20)',
            padding: '13px 14px',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: color.navy,
              lineHeight: '18px',
              margin: 0,
            }}
          >
            {title}
          </p>
          {detail && (
            <p style={{ fontSize: 11.5, lineHeight: '16px', color: color.muted, margin: 0 }}>
              {detail}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 1 }}>
            <button
              ref={yes}
              onClick={onConfirm}
              className="hov-blue"
              style={{
                background: color.blue,
                color: color.white,
                border: 'none',
                fontSize: 12,
                fontWeight: 700,
                padding: '7px 14px',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              {confirmLabel}
            </button>
            <button
              onClick={onCancel}
              className="hov-line"
              style={{
                background: color.white,
                color: color.ink,
                border: `1px solid ${color.line}`,
                fontSize: 12,
                fontWeight: 700,
                padding: '7px 14px',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
          {/* The little pointer back at the control. */}
          <span
            style={{
              position: 'absolute',
              ...(below ? { top: -5 } : { bottom: -5 }),
              [align]: 20,
              width: 8,
              height: 8,
              background: color.white,
              ...(below
                ? { borderLeft: `1px solid ${color.line}`, borderTop: `1px solid ${color.line}` }
                : { borderRight: `1px solid ${color.line}`, borderBottom: `1px solid ${color.line}` }),
              transform: 'rotate(45deg)',
            }}
          />
        </div>,
        document.body,
      )}
    </span>
  );
}

/**
 * A section rule — the heading, then a hairline out to the edge.
 *
 * Used inside the record panels, where `Eyebrow` alone left the sections
 * floating: a run of small grey captions down a long page gives no sense of
 * where one part of the record stops and the next begins.
 */
export function SectionHead({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 4px' }}>
      <span
        style={{
          fontSize: 11.5,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 800,
          color: color.blueDeep,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
      <span style={{ flex: 1, height: 1, background: color.line }} />
    </div>
  );
}

/** Horizontal progress bar — enrollment, traffic share, send progress. */
export function Meter({
  pct,
  height = 7,
  track = color.tint,
}: {
  pct: number;
  height?: number;
  track?: string;
}) {
  return (
    <div style={{ height, background: track, borderRadius: height / 2, overflow: 'hidden' }}>
      <div
        style={{
          height,
          width: `${Math.max(0, Math.min(100, pct))}%`,
          background: color.blue,
          borderRadius: height / 2,
          transition: 'width 0.3s ease',
        }}
      />
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: 60,
        color: color.muted,
        fontSize: 13.5,
      }}
    >
      <IconSpinner /> {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div
      style={{
        background: '#fdf1ee',
        border: '1px solid #f3d5cc',
        color: color.red,
        borderRadius: 14,
        padding: '12px 16px',
        fontSize: 13.5,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}

/**
 * What an action just did, said in place. The green pair the REDCap screens
 * use for "Saved…"; announced, so a screen reader hears it without a focus
 * move.
 */
export function StatusNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      style={{
        background: '#cfe9d8',
        color: '#14563c',
        borderRadius: 14,
        padding: '12px 16px',
        fontSize: 13.5,
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: color.muted, fontSize: 13.5 }}>
      {children}
    </div>
  );
}
